/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from "react";
import samhealthLogo from '../../assets/samhealth.png';
import {
  LayoutDashboard,
  Plus,
  FileText,
  Building,
  Users,
  Calendar,
  AlertTriangle,
  MessageSquare,
  Eye,
  Clock,
  Download,
  RefreshCw,
  Menu,
  X,
  FolderOpen,
} from "lucide-react";
import {
  getHospitalApplications,
  getApplicationStats,
  searchApplications,
  getHospitalDischargeCases,
  getHospitalApplicationsBifurcated,
  getHospitalDisplayStatus,
} from "../../services/applicationService";
import { useAuth } from "../../context/AuthContext";
// removed unused ApplicationsList import
import { SignOutIconButton } from "../../component/common/SignOutButton";
import NotificationBell from "../../components/common/NotificationBell";
import { useNavigate } from "react-router-dom";
import RoleSwitcher from '../../components/common/RoleSwitcher';
import UniversalSearch from '../../components/common/UniversalSearch';

const HospitalDashboard = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allHospitalStage, setAllHospitalStage] = useState('all');
  const [allHospitalStageCounts, setAllHospitalStageCounts] = useState({});
  const [selectedStages, setSelectedStages] = useState(null); // optional multi-stage selection (e.g., combined Approved)
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    underReview: 0,
    approved: 0,
    rejected: 0,
    emergency: 0,
    pendingDocuments: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  // removed unused totalPages
  const [hasMore, setHasMore] = useState(false);
  const [lastDocument, setLastDocument] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    dateRange: "",
    emergency: "",
    priority: "",
    treatmentType: "",
    searchTerm: "",
  });
  const [dischargeCases, setDischargeCases] = useState({ loading: true, items: [], forDate: "" });

  const { userData } = useAuth();
  const hospitalId = userData?.hospitalId;
  const navigate = useNavigate();

  const sidebarItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, count: null },
    { id: "new-application", label: "New Application", icon: Plus, count: null },
    { id: "applications", label: "Applications", icon: FileText, count: stats?.underReview || 0 },
    { id: "hospital-details", label: "Hospital Details", icon: Building, count: null },
    { id: "documents", label: "Documents", icon: FolderOpen, count: null },
  ];

  // removed unused statusColors

  const priorityColors = {
    urgent: "bg-red-100 text-red-800",
    high: "bg-orange-100 text-orange-800",
    normal: "bg-green-100 text-green-800",
  };

  // Load initial data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // effects moved below after function definitions

  const loadApplications = useCallback(async (reset = true) => {
    if (!hospitalId) return;

    setLoading(true);
    try {
      let result;

      if (filters.searchTerm) {
        // Use search function
        result = await searchApplications(hospitalId, filters.searchTerm, {
          status: filters.status || undefined,
          emergency: filters.emergency
            ? filters.emergency === "true"
            : undefined,
          priority: filters.priority || undefined,
        });
      } else {
        // Use paginated fetch
        const filterObj = {};
        if (filters.status) filterObj.status = filters.status;
        if (filters.emergency)
          filterObj.emergency = filters.emergency === "true";
        if (filters.priority) filterObj.priority = filters.priority;
        if (filters.treatmentType)
          filterObj.treatmentType = filters.treatmentType;

        result = await getHospitalApplications(
          hospitalId,
          filterObj,
          10,
          reset ? null : lastDocument
        );
      }

      if (result.success) {
        if (reset) {
          setApplications(result.applications);
          setCurrentPage(1);
        } else {
          setApplications((prev) => [...prev, ...result.applications]);
        }

        setHasMore(result.hasMore || false);
        setLastDocument(result.lastDocument || null);

        // removed unused totalPages calculation
      } else {
        console.error("Error loading applications:", result.error);
        setApplications([]);
      }
    } catch (error) {
      console.error("Error loading applications:", error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [hospitalId, filters, lastDocument]);

  // New: Hospital All Applications list with stage tabs
  const loadAllApplicationsBifurcated = useCallback(async (reset = false) => {
    if (!hospitalId) return;
    setLoading(true);
    try {
      // If multiple stages are selected (e.g., Approved combined), fetch and merge
      if (Array.isArray(selectedStages) && selectedStages.length > 0) {
        const stageResults = await Promise.all(
          selectedStages.map((stageKey) => {
            const stageFilters = { ...filters, _hospitalStage: stageKey };
            return getHospitalApplicationsBifurcated(
              hospitalId,
              stageFilters,
              1,
              25
            );
          })
        );

        const successful = stageResults.filter((r) => r && r.success);
        const mergedAppsMap = new Map();
        successful.forEach((r) => {
          (r.applications || []).forEach((a) => {
            mergedAppsMap.set(a.id || a.applicationId, a);
          });
        });

        const mergedApps = Array.from(mergedAppsMap.values());
        // Use counts from any successful response (they should be identical for counts scope)
        const stageCounts = successful[0]?.stageCountsHospital || {};

        setApplications(mergedApps);
        setAllHospitalStageCounts(stageCounts);
        if (reset) setCurrentPage(1);
        // For combined view, disable load more to keep UX simple and accurate
        setHasMore(false);
      } else {
        const apiFilters = { ...filters };
        if (allHospitalStage && allHospitalStage !== 'all') {
          apiFilters._hospitalStage = allHospitalStage;
        }

        const res = await getHospitalApplicationsBifurcated(
          hospitalId,
          apiFilters,
          reset ? 1 : currentPage,
          10
        );

        if (res.success) {
          setApplications((prev) => (reset ? (res.applications || []) : [...prev, ...(res.applications || [])]));
          setAllHospitalStageCounts(res.stageCountsHospital || {});
          if (reset) setCurrentPage(1);
          setHasMore((res.page || 1) < (res.totalPages || 1));
        } else {
          setApplications([]);
          setAllHospitalStageCounts({});
        }
      }
    } catch (e) {
      console.error('Error loading hospital applications (bifurcated):', e);
      setApplications([]);
      setAllHospitalStageCounts({});
    } finally {
      setLoading(false);
    }
  }, [hospitalId, filters, selectedStages, allHospitalStage, currentPage]);

  const loadStats = useCallback(async () => {
    if (!hospitalId) return;

    try {
      const result = await getApplicationStats(hospitalId);
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }, [hospitalId]);

  const loadDischargeCases = useCallback(async () => {
    if (!hospitalId) return;
    setDischargeCases((prev) => ({ ...prev, loading: true }));
    try {
      const res = await getHospitalDischargeCases(hospitalId);
      if (res.success) {
        setDischargeCases({ loading: false, items: res.applications, forDate: res.targetDate });
      } else {
        setDischargeCases({ loading: false, items: [], forDate: "" });
      }
    } catch (e) {
      console.error("Error loading discharge cases:", e);
      setDischargeCases({ loading: false, items: [], forDate: "" });
    }
  }, [hospitalId]);

  // Effects (after functions are defined)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hospitalId) return;
    loadApplications();
    loadStats();
    loadDischargeCases();
  }, [hospitalId, filters]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!hospitalId) return;
    loadAllApplicationsBifurcated(true);
  }, [hospitalId, allHospitalStage, selectedStages]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
    setLastDocument(null);
  };

  const resetFilters = () => {
    setFilters({
      status: "",
      dateRange: "",
      emergency: "",
      priority: "",
      treatmentType: "",
      searchTerm: "",
    });
    setCurrentPage(1);
    setLastDocument(null);
  };

  const loadMoreApplications = () => {
    if (hasMore && !loading) {
      setCurrentPage((prev) => prev + 1);
      loadApplications(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // removed unused getStatusLabel

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600">
            Overview of patient applications and hospital activities
          </p>
        </div>
        <button
          onClick={() => navigate("/hospital/new-application")}
          className="btn-primary"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Application
        </button>
      </div>

      {/* Stats Cards (Minimal + Clickable) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        {(() => {
          const totalCases =
            Object.values(allHospitalStageCounts || {}).reduce((a, b) => a + (Number(b) || 0), 0) || stats.total || 0;
          const underReview = stats.underReview || allHospitalStageCounts.kyp_pending || 0;
          const queryCount = allHospitalStageCounts.query || 0;
          const scrutinyCount = allHospitalStageCounts.scrutiny || 0;
          const dispatchPending = allHospitalStageCounts.dispatch || 0;
          const approvedCombined = (allHospitalStageCounts.kyp_approved || 0) + (allHospitalStageCounts.discharge || 0);

          const cards = [
            {
              key: 'all',
              label: 'Total Cases',
              value: totalCases,
              icon: FileText,
              bg: 'bg-primary-100 text-primary-600',
              onClick: () => {
                setSelectedStages(null);
                setAllHospitalStage('all');
                setActiveSection('applications');
              },
            },
            {
              key: 'kyp_pending',
              label: 'Under Review',
              value: underReview,
              icon: AlertTriangle,
              bg: 'bg-yellow-100 text-yellow-600',
              onClick: () => {
                setSelectedStages(null);
                setAllHospitalStage('kyp_pending');
                setActiveSection('applications');
              },
            },
            {
              key: 'query',
              label: 'Query Pendency',
              value: queryCount,
              icon: MessageSquare,
              bg: 'bg-orange-100 text-orange-700',
              onClick: () => {
                setSelectedStages(null);
                setAllHospitalStage('query');
                setActiveSection('applications');
              },
            },
            {
              key: 'scrutiny',
              label: 'Scrutiny Pending',
              value: scrutinyCount,
              icon: Eye,
              bg: 'bg-purple-100 text-purple-700',
              onClick: () => {
                setSelectedStages(null);
                setAllHospitalStage('scrutiny');
                setActiveSection('applications');
              },
            },
            {
              key: 'dispatch',
              label: 'Dispatch Pending',
              value: dispatchPending,
              icon: Calendar,
              bg: 'bg-sky-100 text-sky-700',
              onClick: () => {
                setSelectedStages(null);
                setAllHospitalStage('dispatch');
                setActiveSection('applications');
              },
            },
            {
              key: 'approved_combined',
              label: 'Approved',
              value: approvedCombined,
              icon: Users,
              bg: 'bg-green-100 text-green-700',
              onClick: () => {
                setSelectedStages(['kyp_approved', 'discharge']);
                setAllHospitalStage('kyp_approved');
                setActiveSection('applications');
              },
            },
          ];

          return cards.map((c) => {
            const IconComp = c.icon;
            return (
              <button
                key={c.key}
                onClick={c.onClick}
                className="bg-white p-4 rounded-lg shadow-soft border border-gray-200 text-left hover:shadow-md transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">{c.label}</p>
                    <p className="text-2xl font-bold text-gray-800">{c.value}</p>
                  </div>
                  <div className={`${c.bg} p-2 rounded-md`}>
                    <IconComp className="h-5 w-5" />
                  </div>
                </div>
              </button>
            );
          });
        })()}
      </div>

      {/* Discharge Cases (Tomorrow) */}
      <div className="bg-white p-6 rounded-lg shadow-soft border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <Clock className="h-5 w-5 text-primary-600 mr-2" />
            Discharge Cases (Tomorrow)
          </h3>
          <div className="text-sm text-gray-500">for {dischargeCases.forDate || "-"}</div>
        </div>

        {dischargeCases.loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-primary-500" />
            <span className="ml-2 text-gray-600">Loading discharge cases...</span>
          </div>
        ) : dischargeCases.items.length === 0 ? (
          <div className="text-center py-6 text-gray-600">No cases scheduled for discharge tomorrow.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doctor</th>
                  
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dischargeCases.items.map((app) => (
                  <tr
                    key={`d_${app.id}`}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/hospital/application/${app.applicationId}`)}
                  >
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{app.applicationId}</div>
                      <div className="text-xs text-gray-500">Admit: {formatDate(app.medical?.admissionDate)}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{app.patient?.name}</div>
                      <div className="text-xs text-gray-500">{app.patient?.age} yrs, {app.patient?.gender}</div>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900">{app.medical?.doctor?.name || '-'}</td>
                    
                    <td className="px-4 py-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${priorityColors[app.priority] || 'bg-gray-100 text-gray-800'}`}>
                        {app.priority || 'normal'}
                      </span>
                      {app.emergency && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Emergency</span>
                      )}
                    </td>
                    
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-soft border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Patient Applications
          </h3>
          <div className="flex items-center space-x-3">
            <button onClick={resetFilters} className="btn-outline text-sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset Filters
            </button>
            <button className="btn-secondary text-sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <input
              type="text"
              placeholder="Search applications..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange("searchTerm", e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="input-field"
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending_documents">Pending Documents</option>
            </select>
          </div>

          <div>
            <select
              value={filters.emergency}
              onChange={(e) => handleFilterChange("emergency", e.target.value)}
              className="input-field"
            >
              <option value="">All Cases</option>
              <option value="true">Emergency</option>
              <option value="false">Non-Emergency</option>
            </select>
          </div>

          <div>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange("priority", e.target.value)}
              className="input-field"
            >
              <option value="">All Priorities</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
            </select>
          </div>

          <div>
            <select
              value={filters.treatmentType}
              onChange={(e) =>
                handleFilterChange("treatmentType", e.target.value)
              }
              className="input-field"
            >
              <option value="">All Treatment Types</option>
              <option value="medical">Medical</option>
              <option value="surgical">Surgical</option>
            </select>
          </div>
        </div>

        {/* Applications Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-primary-500" />
            <span className="ml-2 text-gray-600">Loading applications...</span>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No applications found
            </h3>
            <p className="text-gray-600 mb-4">
              Get started by creating your first patient application.
            </p>
            <button
              onClick={() => navigate("/hospital/new-application")}
              className="btn-primary"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create New Application
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient Details
                    </th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Policy Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estimated Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((app) => {
                    const mvStatus = app?.workflow?.medicalVerification?.status;
                    const policyStatus = app?.workflow?.policyVerification?.status;
                    const hasFinalApproval = !!app?.workflow?.dischargeSettlement?.finalApprovalLetterUrl;
                    const isAnyApproved = (
                      (typeof app?.status === 'string' && app.status.includes('approved')) ||
                      mvStatus === 'approved' ||
                      policyStatus === 'approved' ||
                      hasFinalApproval
                    );
                    const isDoctorKypApproved = mvStatus === 'approved';
                    return (
                    <tr
                      key={app.id}
                      className={`hover:bg-gray-50 cursor-pointer ${isAnyApproved ? 'border-l-4 border-green-400/80' : ''}`}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          e.preventDefault();
                          e.stopPropagation();
                          const url = `${window.location.origin}/hospital/application/${app.applicationId}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                          return;
                        }
                        navigate(`/hospital/application/${app.applicationId}`);
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center flex-wrap gap-1.5">
                          <span className="text-sm font-medium text-gray-900">
                            {app.applicationId}
                          </span>
                          {isAnyApproved && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-200">
                              {isDoctorKypApproved ? 'Doctor KYP approved' : 'Approved'}
                            </span>
                          )}
                          {app.emergency && (
                            <AlertTriangle className="h-4 w-4 text-red-500 ml-1" title="Emergency case" />
                          )}
                          {(app.status === 'dispute_pending' || app.disputeStatus === 'active' || (Array.isArray(app.activeDisputes) && app.activeDisputes.length > 0)) && (
                            <span
                              className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200"
                              title="Query pending"
                              aria-label="Query pending"
                            >
                              <MessageSquare className="h-3 w-3 mr-1" />
                              Query{Array.isArray(app.activeDisputes) && app.activeDisputes.length > 0 ? ` (${app.activeDisputes.length})` : ''}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(app.submittedAt)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {app.patient.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {app.patient.age} years, {app.patient.gender}
                        </div>
                        <div className="text-xs text-gray-500">
                          Dr. {app.medical.doctor.name}
                        </div>
                      </td>

                      

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {app.policy.number}
                        </div>
                        <div className="text-xs text-gray-500">
                          {app.policy.insuranceCompany}
                        </div>
                        <div className="text-xs text-gray-500">
                          {app.policy.type}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const disp = getHospitalDisplayStatus(app);
                          const cls = disp.key === 'query'
                            ? 'bg-yellow-100 text-yellow-800'
                            : disp.key === 'kyp_approved'
                            ? 'bg-green-100 text-green-800'
                            : disp.key === 'discharge_pending'
                            ? 'bg-purple-100 text-purple-800'
                            : disp.key === 'dispatch_pending'
                            ? 'bg-sky-100 text-sky-800'
                            : disp.key === 'final_approved'
                            ? 'bg-indigo-100 text-indigo-800'
                            : disp.key === 'claimed'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-blue-100 text-blue-800';
                          return (
                            <>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>
                                {disp.label}
                              </span>
                              <div className={`text-xs mt-1 ${priorityColors[app.priority]?.replace('bg-','text-').replace('-100','-600')}`}>
                                {app.priority} Priority
                              </div>
                            </>
                          );
                        })()}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(app.medical.estimatedCost || 0)}
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Showing {applications.length} results
              </div>
              {hasMore && (
                <button
                  onClick={loadMoreApplications}
                  disabled={loading}
                  className="btn-primary text-sm"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // removed unused renderNewApplication

  const renderApplications = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">All Applications</h1>
          <p className="text-gray-600">
            View and manage all patient applications
          </p>
        </div>
      </div>

  {/* Tabs: KYP Pending, KYP Approved, Query, Discharge Pending, Discharge, Scrutiny, Dispatch */}
      <div className="bg-white rounded-lg shadow-soft border border-gray-200">
        <div className="border-b border-gray-200 px-4">
          <div className="flex flex-wrap gap-2 p-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'query', label: 'Query' },
              { key: 'kyp_pending', label: 'KYP Pending' },
              { key: 'kyp_approved', label: 'KYP Approved' },
              { key: 'fal_pending', label: 'FAL Pending' },
              { key: 'fal_approved', label: 'FAL Approved' },
              { key: 'enach_pending', label: 'E-NACH Pending' },
              { key: 'scrutiny', label: 'Scrutiny Pending' },
              { key: 'dispatch', label: 'Dispatch Pending' },
              { key: 'settlement', label: 'Settlement' },
              { key: 'rejected', label: 'Rejected' },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  setSelectedStages(null);
                  setAllHospitalStage(t.key);
                  setCurrentPage(1);
                  setLoading(true);
                }}
                className={`px-3 py-2 text-sm rounded-md border ${
                  allHospitalStage === t.key
                    ? 'bg-primary-50 text-primary-700 border-primary-200'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                }`}
              >
                <span>{t.label}</span>
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 h-5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                  {(allHospitalStageCounts && allHospitalStageCounts[t.key]) || 0}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-primary-500" />
              <span className="ml-2 text-gray-600">Loading applications...</span>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No applications found</h3>
              <p className="text-gray-600 mb-4">Try changing tabs or filters</p>
              <button onClick={() => navigate("/hospital/new-application")} className="btn-primary">
                <Plus className="h-5 w-5 mr-2" /> Create New Application
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Application</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                    
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated Cost</th>
                    
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {applications.map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/hospital/application/${app.applicationId}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{app.applicationId}</div>
                        <div className="text-xs text-gray-500">{app.submittedAt ? (app.submittedAt.toDate ? app.submittedAt.toDate().toLocaleDateString('en-IN') : new Date(app.submittedAt).toLocaleDateString('en-IN')) : '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{app.patient?.name}</div>
                        <div className="text-sm text-gray-500">{app.patient?.age} yrs, {app.patient?.gender}</div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const disp = getHospitalDisplayStatus(app);
                          const cls = disp.key === 'query'
                            ? 'bg-yellow-100 text-yellow-800'
                            : disp.key === 'kyp_approved'
                            ? 'bg-green-100 text-green-800'
                            : disp.key === 'discharge_pending'
                            ? 'bg-purple-100 text-purple-800'
                            : disp.key === 'dispatch_pending'
                            ? 'bg-sky-100 text-sky-800'
                            : disp.key === 'final_approved'
                            ? 'bg-indigo-100 text-indigo-800'
                            : disp.key === 'claimed'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-blue-100 text-blue-800';
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${cls}`}>{disp.label}</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">₹{(app.medical?.estimatedCost || 0).toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="text-sm text-gray-500">Showing {applications.length} results</div>
          {hasMore && (
            <button
              onClick={() => {
                setCurrentPage((p) => p + 1);
                loadAllApplicationsBifurcated(false);
              }}
              disabled={loading}
              className="btn-primary text-sm"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // removed unused renderHospitalDetails

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboard();
      case "applications":
        return renderApplications();
      default:
        return renderDashboard();
    }
  };

  if (!hospitalId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600">
            Hospital information not found. Please contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center px-6 py-4">
          <div className="flex items-center space-x-4 min-w-0">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-600 hover:text-gray-900 lg:hidden"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3">
                <img src={samhealthLogo} alt="SamHealth" className="h-8 w-auto object-contain" />
                <p className="text-sm text-teal-600">Hospital Portal</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 flex-1 justify-end">
            {/* Universal Search Bar */}
            <div className="w-64 hidden md:block">
              <UniversalSearch userRole="hospital" />
            </div>

            <div className="flex items-center space-x-4 flex-shrink-0">
            <NotificationBell />
            <div className="text-right">
              <p className="text-sm font-medium text-gray-800">
                {userData?.hospitalName || "Hospital"}
              </p>
              <p className="text-xs text-gray-600">
                {userData?.profile?.designation || "Administrator"}
              </p>
            </div>
            <RoleSwitcher buttonClassName="border-gray-300 hover:border-teal-400 hover:text-teal-700" />
            {/* Removed decorative header icon */}
            <SignOutIconButton
              size="sm"
              showConfirmModal={true}
              className="hover:bg-red-100"
            />
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } fixed lg:sticky lg:translate-x-0 z-30 w-64 top-16 lg:top-16 h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] bg-white shadow-lg border-r border-gray-200 transition-transform duration-300 ease-in-out overflow-y-auto`}
        >
          <nav className="mt-8">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === "new-application") {
                      navigate("/hospital/new-application");
                    } else if (item.id === "documents") {
                      navigate("/documents");
                    } else {
                      setActiveSection(item.id);
                    }
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors duration-200 ${
                    isActive
                      ? "bg-primary-50 text-primary-600 border-r-2 border-primary-500"
                      : "text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </div>
                  {item.count !== null && item.count > 0 && (
                    <span className="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded-full">
                      {item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:ml-0 ml-64">{renderContent()}</main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default HospitalDashboard;
